import { useEffect, useRef } from 'react';
import { Label, Radio } from '@shared/components';
import type { ArrowKeyReveal } from '../hooks/useArrowKeyReveal';
import type { FundingChoice } from '../utils/invoicePanel';

interface FundingChoiceCardsProps {
    question: string;
    choice: FundingChoice | null;
    locked: boolean;
    potTotalAvailable: boolean;
    reveal: ArrowKeyReveal<FundingChoice>;
    focusOnMount?: boolean;
}

const GROUP_LABEL_ID = 'invoice-funding-choice-label';
const RADIO_NAME = 'invoice-funding-choice';

const OPTIONS: ReadonlyArray<{ value: FundingChoice; id: string; label: string }> = [
    { value: 'top_up', id: 'invoice-funding-top_up', label: 'Customer tops up' },
    { value: 'arrears', id: 'invoice-funding-load', label: 'Load into arrears' },
    { value: 'pot_total', id: 'invoice-funding-pot', label: 'Invoice as pot total' },
];

function fundingCardClass(checked: boolean): string {
    const tone = checked
        ? 'border-mp-primary bg-mp-primary-content'
        : 'border-mp-border';
    return `rounded-lg border p-3 ${tone}`;
}

export function FundingChoiceCards({ question, choice, locked, potTotalAvailable, reveal, focusOnMount = false }: FundingChoiceCardsProps) {
    const group = useRef<HTMLDivElement>(null);
    const focusFirst = useRef(focusOnMount);

    useEffect(() => {
        if (focusFirst.current) group.current?.querySelector<HTMLInputElement>('input:not(:disabled)')?.focus();
    }, []);

    return (
        <div
            ref={group}
            role="radiogroup"
            aria-labelledby={GROUP_LABEL_ID}
            className="space-y-2"
            onKeyDown={reveal.onKeyDown}
            onKeyUp={reveal.onKeyUp}
        >
            <Label id={GROUP_LABEL_ID}>{question}</Label>
            {OPTIONS.filter(({ value }) => value !== 'pot_total' || potTotalAvailable).map(({ value, id, label }) => (
                <Radio
                    key={value}
                    id={id}
                    name={RADIO_NAME}
                    value={value}
                    label={label}
                    checked={choice === value}
                    disabled={locked}
                    className={fundingCardClass(choice === value)}
                    onChange={() => reveal.choose(value)}
                />
            ))}
        </div>
    );
}
